import { useState } from "react";
import Markdown from "react-markdown";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
// see https://github.com/PrismJS/prism-themes?tab=readme-ov-file for all themes
// NOTE: I would prefer hljs -> github dark min
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import tsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx";
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript";
import scss from "react-syntax-highlighter/dist/esm/languages/prism/scss";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import markdown from "react-syntax-highlighter/dist/esm/languages/prism/markdown";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import lua from "react-syntax-highlighter/dist/esm/languages/prism/lua";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import java from "react-syntax-highlighter/dist/esm/languages/prism/java";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx";
import ruby from "react-syntax-highlighter/dist/esm/languages/prism/ruby";
import rust from "react-syntax-highlighter/dist/esm/languages/prism/rust";
import go from "react-syntax-highlighter/dist/esm/languages/prism/go";
import php from "react-syntax-highlighter/dist/esm/languages/prism/php";
import { CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";

// Polyfills
import "core-js/features/object/has-own";
import "core-js/features/promise/all-settled";
import useUtmify from "../utm";
import remarkGfm from "remark-gfm";

SyntaxHighlighter.registerLanguage("tsx", tsx);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("scss", scss);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("markdown", markdown);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("lua", lua);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("jsx", jsx);
SyntaxHighlighter.registerLanguage("ruby", ruby);
SyntaxHighlighter.registerLanguage("rust", rust);
SyntaxHighlighter.registerLanguage("go", go);
SyntaxHighlighter.registerLanguage("php", php);

function Pre(pre: any) {
  const codeChunk = pre.node.children[0].children[0].value; // string
  const [showCopy, setShowCopy] = useState<string>("copy");

  const language = pre.node?.children[0]?.properties.className
    ?.filter((n: string) => n.includes("language-"))?.[0]
    .substring("language-".length);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeChunk);
      setShowCopy("success");
    } catch (error) {
      setShowCopy("failure");
    } finally {
      setTimeout(() => setShowCopy("copy"), 1500);
    }
  };

  return (
    <div className="relative h-full not-tight">
      <div className="absolute z-50 w-full px-2">
        <div className="flex items-center justify-between text-chat-text">
          <span className="text-sm">{language || ""}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="hover:text-gray-300 transition-colors"
          >
            {showCopy === "copy" && (
              <DocumentDuplicateIcon className="h-5 w-5" />
            )}
            {showCopy === "success" && (
              <CheckIcon className="h-5 w-5 text-green-400" />
              // <span className="text-xs font-medium text-green-400">
              //   Copied!
              // </span>
            )}
            {showCopy === "failure" && (
              <span className="text-xs font-medium text-red-400">Failed!</span>
            )}
          </button>
        </div>
      </div>

      <div className="relative w-full overflow-x-auto">
        <pre {...pre} />
      </div>
    </div>
  );
}

const customOneDark = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: "rgb(30 41 59)", // slate-800 RGB value
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: "rgb(30 41 59)", // slate-800 RGB value
  },
};

function Code(props: any) {
  const { inline, children, className, node, ...rest } = props;
  const match = /language-(\w+)/.exec(className || "");
  return !inline && match ? (
    <SyntaxHighlighter
      {...rest}
      PreTag="pre"
      language={match[1]}
      style={customOneDark}
      className="not-prose not-tight scrollbar-thin scrollbar-track-base-content/5 scrollbar-thumb-base-content/40 scrollbar-track-rounded-md scrollbar-thumb-rounded"
      showLineNumbers={false}
      useInlineStyles={true}
    >
      {String(children).replace(/\n$/, "")}
    </SyntaxHighlighter>
  ) : (
    <code {...rest} className={className}>
      {children}
    </code>
  );
}

// TODO(liamvdv): add a security check to ensure that the link is not malicious
//                / collect link data via API.
function ExternalLink(props: any) {
  const utmify = useUtmify();
  let href = props.href;
  try {
    // NOTE(memben): do not utmify mailto:, tel: or other non-http links
    if (props.href.startsWith("http")) {
      href = utmify(props.href, "message_link");
    }
  } catch (error) {
    // Silently fall back to original href
    console.warn("Failed to utmify link:", error);
  }

  const isCiteTag =
    typeof props.children === "string" && props.children.startsWith("cite:");

  if (isCiteTag) {
    return (
      <sup>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="no-underline inline-flex items-center rounded-md bg-gray-200 text-xs px-1 text-gray-600 mx-0.5 transition-colors hover:bg-brand hover:text-chat"
          {...props}
        >
          {props.children.slice(5)}
        </a>
      </sup>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="no-underline text-blue-700 px-1 rounded transition-colors hover:text-blue-500 focus:outline-brand"
      {...props}
    >
      {props.children}
    </a>
  );
}

function MarkdownRenderer({ content }: { content: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        pre: Pre,
        code: Code,
        a: ExternalLink,
      }}
      //   remarkPlugins={[remarkMath]}
      //   rehypePlugins={[rehypeKatex]}
    >
      {content}
    </Markdown>
  );
}

export default MarkdownRenderer;
